/**
 * OHT ↔ external load port ↔ internal lifter ↔ shelf transfer animation.
 *
 * DROP (store): OHT → external load port → lifter pick → shelf
 *   0.00–0.35  OHT aligns, lowers FOUP onto external load port, releases
 *   0.35–0.50  FOUP confirmed on load port; lifter descends to port level
 *   0.50–1.00  Lifter picks from port → vertical travel → shelf place
 *
 * PICKUP (retrieve): shelf → lifter → external load port → OHT
 *   0.00–0.45  Lifter retrieves from shelf → places FOUP on external load port
 *   0.45–0.75  FOUP waits on load port; OHT approaches and lowers
 *   0.75–1.00  OHT lifts FOUP; lifter returns home
 *
 * OHT NEVER places FOUP directly inside the stocker — always via external port.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { useAppSelector } from '../../../../hooks/redux';
import { replaySnapshotStore } from '../../../../store/replaySnapshotStore';
import { FOUP } from '../FOUP/FOUP';
import { resolveKitFoupScale } from '../types';
import {
  StockerLoadPortBank,
  getStockerLoadPortFoupPose,
  getStockerLoadPortLocalPositions,
  resolveKitPortForTransfer,
  type LayoutStockerPortRef,
  type StockerLoadPortKind,
} from './StockerLoadPortBank';
import { StockerCrane, type StockerCranePose } from './StockerCrane';
import {
  committedDropRackCount,
  pickupRackDisplayCount,
  DROP_OHT_RELEASE,
  DROP_PORT_REST_END,
  PICKUP_PORT_READY,
  STOCKER_PICKUP_OHT_TAKE_PROGRESS,
} from './stockerTransferTiming';
import { liveInventoryDisplayStore } from '../../../../store/liveInventoryDisplayStore';
import {
  findLiveStocker,
  liveStockerTotalOccupancy,
} from '../../../../features/simulation/liveStockerInventory';
import { resolveColumnKind, type Port } from '../../../../utils/ports';
import {
  mapTransferMotionToRobotState,
  resolveStockerRobotParkState,
} from './stockerTransferRobotMapping';
import { stockerTransferRobotStore } from './stockerTransferRobotStore';
import { stockerTransferLifterStore, STOCKER_LIFTER_IDLE, type StockerLifterTransferState } from './stockerTransferLifterStore';
import { stockerRackDisplayStore } from './stockerRackDisplayStore';
import { liveOhtRuntimeStore } from '../../../../store/liveOhtRuntimeStore';
import { transferAnimationPipelineStore } from '../../../../store/transferAnimationPipelineStore';
import { resolveTransferStationId } from '../../../../features/simulation/transferStationId';
import { resolveStockerDimensions } from './stockerTypes';

export type StockerTransferAnimProps = {
  width: number;
  height: number;
  depth: number;
  elementId?: string;
  /** Aliases for matching live inventory rows (layout name, amhsId, etc.). */
  stockerAliases?: readonly string[];
  bayCount?: string | number;
  outsideStocker?: boolean;
  storedFoupsCount: number;
  totalSlots: number;
  slotPositions: Array<[number, number, number]>;
  /** Constant FOUP scale (port / crane / rack). */
  foupScale?: number;
  opacity?: number;
  onRackDisplayCount?: (count: number) => void;
  /** When false, load ports are drawn by `StockerLoadPortsLayer3D` instead. */
  showLoadPortBank?: boolean;
  /** When false, drive `StockerRobot` via transfer store instead of legacy crane mesh. */
  showCrane?: boolean;
  /** Publish lifter pose for procedural Stocker3D (keyed by elementId). */
  driveRobot?: boolean;
  onTransferActiveChange?: (active: boolean) => void;
  onTransferColumnKind?: (kind: 'raw' | 'processed' | null) => void;
};

type CraneMotion = {
  carriage: [number, number, number];
  forkZ: number;
  foup: [number, number, number];
  forkVisible: boolean;
  /** FOUP still riding the fork (not yet deposited). */
  carrying: boolean;
};

function ease(t: number): number {
  const u = Math.min(1, Math.max(0, t));
  return u * u * (3 - 2 * u);
}

function slotIndexFromId(slotId: string | undefined, slotCount: number): number | null {
  if (!slotId || slotCount <= 0) return null;
  const match = /(\d+)\s*$/.exec(slotId);
  if (!match) return null;
  const oneBased = Number(match[1]);
  if (!Number.isInteger(oneBased) || oneBased <= 0) return null;
  return Math.max(0, Math.min(slotCount - 1, oneBased - 1));
}

function lerp3(
  a: [number, number, number],
  b: [number, number, number],
  t: number
): [number, number, number] {
  const e = ease(t);
  return [a[0] + (b[0] - a[0]) * e, a[1] + (b[1] - a[1]) * e, a[2] + (b[2] - a[2]) * e];
}

function slotFoupPose(
  slotPos: [number, number, number],
  /** 0 when slot positions already include the shelf deck offset. */
  trayLift = 0
): [number, number, number] {
  return [slotPos[0], slotPos[1] + trayLift, slotPos[2]];
}

/** FOUP sits on the fork cradle deck. */
function foupOnFork(
  carriage: [number, number, number],
  forkZ: number,
  deck: number
): [number, number, number] {
  return [carriage[0], carriage[1] + deck, forkZ];
}

/**
 * One FOUP mesh: carrier on the port tray, or StockerRobot on the fork — never both.
 * `robotOwnsFoup` is true once the lifter has engaged the FOUP (avoids a one-frame pop at pick).
 */
export function shouldShowTransferCarrierFoup(
  visible: boolean,
  robotOwnsFoup: boolean,
  driveRobot: boolean
): boolean {
  return visible && !(driveRobot && robotOwnsFoup);
}

/**
 * Store path (u 0→1): FOUP starts on load port, ends seated in rack slot.
 * Clear staged motion so the crane pick → travel → place reads clearly.
 */
function resolveStoreCrane(
  uRaw: number,
  port: [number, number, number],
  slot: [number, number, number],
  aisleZ: number,
  forkDeck: number
): CraneMotion {
  const u = Math.min(1, Math.max(0, uRaw));
  const portY = port[1];
  const slotY = slot[1];

  // 0–0.2: extend fork to port (FOUP still on load-port tray)
  if (u < 0.2) {
    const e = ease(u / 0.2);
    return {
      carriage: [port[0], portY, aisleZ],
      forkZ: aisleZ + (port[2] - aisleZ) * e,
      foup: [port[0], portY, port[2]],
      forkVisible: true,
      carrying: false,
    };
  }

  // 0.2–0.38: lift FOUP onto fork and retract to aisle (blend tray → fork so FOUP never pops)
  if (u < 0.38) {
    const e = ease((u - 0.2) / 0.18);
    const z = port[2] + (aisleZ - port[2]) * e;
    const carriage: [number, number, number] = [port[0], portY, aisleZ];
    const trayFoup: [number, number, number] = [port[0], portY, port[2]];
    const forkFoup = foupOnFork(carriage, z, forkDeck);
    return {
      carriage,
      forkZ: z,
      foup: lerp3(trayFoup, forkFoup, e),
      forkVisible: true,
      carrying: e > 0.08,
    };
  }

  // 0.38–0.72: aisle travel to slot column / height (FOUP locked on fork)
  if (u < 0.72) {
    const e = ease((u - 0.38) / 0.34);
    const c = lerp3([port[0], portY, aisleZ], [slot[0], slotY, aisleZ], e);
    return {
      carriage: c,
      forkZ: aisleZ,
      foup: foupOnFork(c, aisleZ, forkDeck),
      forkVisible: true,
      carrying: true,
    };
  }

  // 0.72–0.88: fork into slot with FOUP
  if (u < 0.88) {
    const e = ease((u - 0.72) / 0.16);
    const z = aisleZ + (slot[2] - aisleZ) * e;
    const carriage: [number, number, number] = [slot[0], slotY, aisleZ];
    return {
      carriage,
      forkZ: z,
      foup: foupOnFork(carriage, z, forkDeck),
      forkVisible: true,
      carrying: true,
    };
  }

  // 0.88–1.0: deposit FOUP centered in slot, retract fork
  const e = ease((u - 0.88) / 0.12);
  const z = slot[2] + (aisleZ - slot[2]) * e;
  return {
    carriage: [slot[0], slotY, aisleZ],
    forkZ: z,
    foup: [slot[0], slotY, slot[2]],
    forkVisible: true,
    carrying: false,
  };
}

/**
 * Retrieve path (u 0→1): FOUP starts in rack slot, ends resting on load port.
 */
function resolveRetrieveCrane(
  uRaw: number,
  port: [number, number, number],
  slot: [number, number, number],
  aisleZ: number,
  forkDeck: number
): CraneMotion {
  const u = Math.min(1, Math.max(0, uRaw));
  const portY = port[1];
  const slotY = slot[1];

  // 0–0.12: move carriage to slot, extend fork to FOUP
  if (u < 0.12) {
    const e = ease(u / 0.12);
    return {
      carriage: [slot[0], slotY, aisleZ],
      forkZ: aisleZ + (slot[2] - aisleZ) * e,
      foup: [slot[0], slotY, slot[2]],
      forkVisible: true,
      carrying: false,
    };
  }

  // 0.12–0.28: pull FOUP from slot onto fork (blend slot → fork cradle)
  if (u < 0.28) {
    const e = ease((u - 0.12) / 0.16);
    const z = slot[2] + (aisleZ - slot[2]) * e;
    const carriage: [number, number, number] = [slot[0], slotY, aisleZ];
    const slotFoup: [number, number, number] = [slot[0], slotY, slot[2]];
    const forkFoup = foupOnFork(carriage, z, forkDeck);
    return {
      carriage,
      forkZ: z,
      foup: lerp3(slotFoup, forkFoup, e),
      forkVisible: true,
      carrying: e > 0.08,
    };
  }

  // 0.28–0.68: aisle travel toward load port
  if (u < 0.68) {
    const e = ease((u - 0.28) / 0.4);
    const c = lerp3([slot[0], slotY, aisleZ], [port[0], portY, aisleZ], e);
    return {
      carriage: c,
      forkZ: aisleZ,
      foup: foupOnFork(c, aisleZ, forkDeck),
      forkVisible: true,
      carrying: true,
    };
  }

  // 0.68–0.85: extend fork and place FOUP on load port (blend fork → tray)
  if (u < 0.85) {
    const e = ease((u - 0.68) / 0.17);
    const z = aisleZ + (port[2] - aisleZ) * e;
    const carriage: [number, number, number] = [port[0], portY, aisleZ];
    const forkFoup = foupOnFork(carriage, z, forkDeck);
    const trayFoup: [number, number, number] = [port[0], portY, port[2]];
    return {
      carriage,
      forkZ: z,
      foup: lerp3(forkFoup, trayFoup, e),
      forkVisible: true,
      carrying: e < 0.92,
    };
  }

  // 0.85–1.0: FOUP seated on load-port tray; retract fork
  const e = ease((u - 0.85) / 0.15);
  return {
    carriage: [port[0], portY, aisleZ],
    forkZ: port[2] + (aisleZ - port[2]) * e,
    foup: [port[0], portY, port[2]],
    forkVisible: true,
    carrying: false,
  };
}

export const StockerTransferAnim: React.FC<StockerTransferAnimProps> = ({
  width,
  height,
  depth,
  elementId,
  stockerAliases = [],
  bayCount,
  outsideStocker = false,
  storedFoupsCount,
  totalSlots,
  slotPositions,
  foupScale: foupScaleProp,
  opacity = 1,
  onRackDisplayCount,
  showLoadPortBank = true,
  showCrane = true,
  driveRobot = false,
  onTransferActiveChange,
  onTransferColumnKind,
}) => {
  const running = useAppSelector((s) => s.simulation.running);
  const source = useAppSelector((s) => s.simulation.source);
  const elements = useAppSelector((s) => s.layout.elements);
  const browserKinetic = useAppSelector((s) => s.simulation.browserKinetic);
  const stockerPortOwners = browserKinetic?.stockerPortOwners;

  const layoutPorts = useMemo((): LayoutStockerPortRef[] => {
    const stockerEl = elementId ? elements[elementId] : null;
    const ports = Array.isArray(stockerEl?.customProperties?.ports)
      ? (stockerEl!.customProperties!.ports as LayoutStockerPortRef[])
      : [];
    return ports;
  }, [elementId, elements]);

  const portLocal = useMemo(
    () =>
      getStockerLoadPortLocalPositions(width, height, depth, {
        bayCount,
        outsideStocker,
        layoutPorts,
      }),
    [width, height, depth, bayCount, outsideStocker, layoutPorts]
  );

  const dims = useMemo(
    () => resolveStockerDimensions(width, height, depth),
    [width, height, depth]
  );
  const aisleZ = dims.lifterAisleZ;
  const parkY = dims.loadPortDeckY;
  const parkX = dims.lifterX;

  const stockerPortIds = useMemo(() => {
    const set = new Set<string>();
    for (const p of layoutPorts) {
      if (typeof p?.id === 'string' && p.id.length > 0) set.add(p.id);
    }
    if (stockerPortOwners && elementId) {
      for (const [portId, sId] of Object.entries(stockerPortOwners)) {
        if (sId === elementId) set.add(portId);
      }
    }
    return set;
  }, [elementId, layoutPorts, stockerPortOwners]);
  const stockerOwnerIds = useMemo(() => {
    const ids = new Set<string>();
    if (elementId) ids.add(elementId);
    const stockerEl = elementId ? elements[elementId] : null;
    const amhsId = stockerEl?.customProperties?.amhsId;
    const stockerId = stockerEl?.customProperties?.stockerId;
    if (typeof amhsId === 'string' && amhsId.length > 0) ids.add(amhsId);
    if (typeof stockerId === 'string' && stockerId.length > 0) ids.add(stockerId);
    const name = stockerEl?.metadata?.name;
    if (typeof name === 'string' && name.length > 0) ids.add(name);
    for (const alias of stockerAliases) {
      if (alias) ids.add(alias);
    }
    for (const portId of stockerPortIds) {
      const ownerId = stockerPortOwners?.[portId];
      if (ownerId) ids.add(ownerId);
    }
    return ids;
  }, [elementId, elements, stockerAliases, stockerPortIds, stockerPortOwners]);

  const carrierRef = useRef<THREE.Group>(null);
  const cranePoseRef = useRef<StockerCranePose>({
    carriage: [parkX, parkY, aisleZ],
    forkZ: aisleZ,
    forkVisible: false,
    active: false,
  });
  const [carrierVisible, setCarrierVisible] = useState(false);
  const [occupiedPorts, setOccupiedPorts] = useState<
    Partial<Record<StockerLoadPortKind, boolean>>
  >({});
  const prevCountRef = useRef(storedFoupsCount);
  const transferStartCountRef = useRef(storedFoupsCount);
  const activePortIdRef = useRef<string | undefined>(undefined);
  const wasActiveRef = useRef(false);
  const onRackRef = useRef(onRackDisplayCount);
  useEffect(() => {
    onRackRef.current = onRackDisplayCount;
  }, [onRackDisplayCount]);
  onRackRef.current = onRackDisplayCount;
  const onTransferActiveRef = useRef(onTransferActiveChange);
  onTransferActiveRef.current = onTransferActiveChange;
  const lastTransferActiveRef = useRef(false);
  const onTransferColumnKindRef = useRef(onTransferColumnKind);
  onTransferColumnKindRef.current = onTransferColumnKind;
  const lastTransferColumnKindRef = useRef<'raw' | 'processed' | null>(null);
  const visibleRef = useRef(false);
  const occupiedKeyRef = useRef('');
  const lastRackRef = useRef(storedFoupsCount);
  const craneCompleteEventRef = useRef<string | null>(null);

  useEffect(() => {
    if (!elementId) return;
    return () => {
      if (driveRobot) stockerTransferRobotStore.clear(elementId);
      stockerTransferLifterStore.clear(elementId);
      stockerRackDisplayStore.clear(elementId);
    };
  }, [driveRobot, elementId]);

  // One FOUP scale for port, rack, and crane — matches OHT drop size.
  const foupScale = foupScaleProp ?? resolveKitFoupScale(width, height, depth);
  const forkDeck = Math.max(0.22, foupScale * 0.048);

  // Outside live playback there is no replay timeline, so sync directly.
  useEffect(() => {
    if (!running && !wasActiveRef.current) {
      const rack = Math.min(Math.max(0, storedFoupsCount), totalSlots);
      if (elementId) stockerRackDisplayStore.set(elementId, rack);
      onRackRef.current?.(rack);
      lastRackRef.current = rack;
      prevCountRef.current = storedFoupsCount;
    }
  }, [running, storedFoupsCount, totalSlots, elementId]);

  useFrame(() => {
    const snapshot = replaySnapshotStore.getSnapshot();
    const stockerReplay = [...stockerOwnerIds]
      .map((ownerId) => snapshot?.stockers[ownerId])
      .find(Boolean);
    // Read held inventory every frame so rack dots never snap back to a stale
    // React prop when a transfer ends between UI sync ticks.
    const heldLiveOcc =
      source === 'live' && elementId
        ? liveStockerTotalOccupancy(
            liveInventoryDisplayStore.getSnapshot(),
            elementId,
            stockerAliases
          )
        : undefined;
    const rackCount = Math.min(
      Math.max(0, heldLiveOcc ?? storedFoupsCount),
      totalSlots
    );
    const slots = slotPositions;

    const portIds = new Set(stockerPortIds);
    if (source === 'live' && elementId) {
      const liveStocker = findLiveStocker(
        liveInventoryDisplayStore.getSnapshot(),
        elementId,
        stockerAliases
      );
      for (const port of liveStocker?.ports ?? []) {
        if (port.loadPortId) portIds.add(port.loadPortId);
      }
    }

    let active: {
      progress: number;
      isPickup: boolean;
      transferPortId?: string;
      slotId?: string;
      eventId?: string;
      stationId?: string;
    } | null = null;

    const matchesStockerTransfer = (transfer: {
      portId: string;
      stationId?: string;
    }) =>
      portIds.has(transfer.portId) ||
      (!!transfer.stationId && stockerOwnerIds.has(transfer.stationId));

    const liveTransfer =
      source === 'live'
        ? liveOhtRuntimeStore.getActiveTransfer({
            portIds,
            stationIds: stockerOwnerIds,
          })?.transfer ??
          Object.values(liveOhtRuntimeStore.getFrame().byOhtId)
            .map((runtime) => runtime.transfer)
            .filter(
              (transfer): transfer is NonNullable<typeof transfer> =>
                !!transfer && matchesStockerTransfer(transfer)
            )
            .sort((a, b) => b.progress01 - a.progress01)[0] ??
          null
        : null;
    const replayTransfer = source === 'live' ? undefined : stockerReplay?.activeTransfer;
    if (liveTransfer) {
      active = {
        progress: liveTransfer.progress01,
        isPickup: liveTransfer.action === 'PICKUP',
        transferPortId: liveTransfer.portId,
        eventId: liveTransfer.eventId,
        stationId: liveTransfer.stationId,
      };
    } else if (
      replayTransfer &&
      replayTransfer.delta !== 0 &&
      (replayTransfer.action === 'PICKUP' || replayTransfer.action === 'DROP') &&
      !!replayTransfer.portId &&
      stockerPortIds.has(replayTransfer.portId)
    ) {
      active = {
        progress: Math.min(1, Math.max(0, replayTransfer.progress01)),
        isPickup: replayTransfer.action === 'PICKUP',
        transferPortId: replayTransfer.portId,
        slotId: replayTransfer.slotId,
      };
    }

    // Idle stockers: park once, then skip per-frame crane/FOUP work until a
    // transfer starts or the rack count changes.
    if (!active && !wasActiveRef.current) {
      if (
        lastRackRef.current === rackCount &&
        !visibleRef.current &&
        occupiedKeyRef.current === ''
      ) {
        return;
      }
    }

    if (active && !wasActiveRef.current) {
      // Snapshot inventory is the count immediately before this operation;
      // never seed an animation from the response's final count.
      if (!active.isPickup && rackCount > prevCountRef.current) {
        transferStartCountRef.current = prevCountRef.current;
      } else {
        transferStartCountRef.current = rackCount;
      }
      activePortIdRef.current = active.transferPortId;
    }
    wasActiveRef.current = !!active;
    if (lastTransferActiveRef.current !== wasActiveRef.current) {
      lastTransferActiveRef.current = wasActiveRef.current;
      onTransferActiveRef.current?.(wasActiveRef.current);
    }

    const activePortId = active?.transferPortId ?? activePortIdRef.current;
    let transferColumnKind: 'raw' | 'processed' | null = null;
    if (active && activePortId) {
      const layoutPort = layoutPorts.find((port) => port.id === activePortId);
      if (layoutPort) {
        const kind = resolveColumnKind(layoutPort as Port, 'stocker');
        if (kind === 'raw' || kind === 'processed') transferColumnKind = kind;
      }
    }
    if (lastTransferColumnKindRef.current !== transferColumnKind) {
      lastTransferColumnKindRef.current = transferColumnKind;
      onTransferColumnKindRef.current?.(transferColumnKind);
    }

    if (!active) {
      prevCountRef.current = rackCount;
      activePortIdRef.current = undefined;
      craneCompleteEventRef.current = null;
    }

    const markCraneCompleteIfReady = (
      progress: number,
      isPickup: boolean,
      eventId?: string,
      stationId?: string,
      portId?: string
    ) => {
      if (!eventId || craneCompleteEventRef.current === eventId) return;
      const craneDone = isPickup
        ? progress >= PICKUP_PORT_READY
        : progress >= 1;
      if (!craneDone) return;
      const pipelineStationId = resolveTransferStationId({
        stockerId: stationId,
        portId,
      });
      const candidates = new Set<string>();
      if (pipelineStationId) candidates.add(pipelineStationId);
      if (stationId) candidates.add(stationId);
      for (const ownerId of stockerOwnerIds) candidates.add(ownerId);
      craneCompleteEventRef.current = eventId;
      for (const sid of candidates) {
        transferAnimationPipelineStore.markCranePhaseComplete(sid, eventId);
      }
    };

    const kitKind = resolveKitPortForTransfer(
      portLocal,
      layoutPorts,
      active?.transferPortId ?? activePortIdRef.current
    );
    const portBase =
      portLocal[kitKind] ??
      ([parkX, dims.loadPortDeckY, dims.loadPortZ] as [number, number, number]);
    const portPose = getStockerLoadPortFoupPose(portBase, height, width, depth);

    const startCount = Math.min(Math.max(0, transferStartCountRef.current), totalSlots);
    const explicitSlotIndex = slotIndexFromId(active?.slotId, slots.length);
    const targetIdx =
      explicitSlotIndex ??
      (active?.isPickup
        ? Math.max(0, Math.min(Math.max(startCount - 1, 0), slots.length - 1))
        : Math.max(0, Math.min(startCount, slots.length - 1)));
    const slotPos = slots[targetIdx] ?? ([0, height * 0.35, 0] as [number, number, number]);
    const slotPose = slotFoupPose(slotPos);

    let pos: [number, number, number] | null = null;
    let visible = false;
    const scale = foupScale;
    let resting: Partial<Record<StockerLoadPortKind, boolean>> = {};
    let rackDisplay: number;
    let carriage: [number, number, number];
    let forkZ = aisleZ;
    let forkVisible = false;
    let carrying = false;
    let robotOwnsFoup = false;

    if (active) {
      const p = active.progress;
      const restPort: Partial<Record<StockerLoadPortKind, boolean>> = { [kitKind]: true };

      const pickupRack = pickupRackDisplayCount(p, startCount, rackCount, totalSlots);

      if (active.isPickup) {
        // —— PICKUP: rack → crane → load port → OHT ——
        // Inventory dot stays until the OHT takes the FOUP from the load port.
        if (startCount <= 0 && p < PICKUP_PORT_READY) {
          visible = false;
          rackDisplay = pickupRack;
          carriage = [parkX, parkY, aisleZ];
        } else if (p < PICKUP_PORT_READY) {
          const u = p / PICKUP_PORT_READY;
          const motion = resolveRetrieveCrane(u, portPose, slotPose, aisleZ, forkDeck);
          pos = motion.foup;
          visible = true;
          rackDisplay = pickupRack;
          carriage = motion.carriage;
          forkZ = motion.forkZ;
          forkVisible = motion.forkVisible;
          carrying = motion.carrying;
          robotOwnsFoup = driveRobot && motion.carrying && u >= 0.16;
          resting = !motion.carrying && u > 0.85 ? restPort : {};
        } else if (p + 1e-6 < STOCKER_PICKUP_OHT_TAKE_PROGRESS) {
          // External lifter owns the FOUP while lifting to track — avoid duplicating on the port tray.
          visible = false;
          resting = {};
          rackDisplay = pickupRack;
          carriage = [portPose[0], portPose[1], aisleZ];
          forkZ = aisleZ;
          forkVisible = false;
        } else {
          visible = false;
          resting = {};
          rackDisplay = pickupRack;
          carriage = lerp3(
            [portPose[0], portPose[1], aisleZ],
            [parkX, parkY, aisleZ],
            ease(
              (p - STOCKER_PICKUP_OHT_TAKE_PROGRESS) /
                Math.max(0.001, 1 - STOCKER_PICKUP_OHT_TAKE_PROGRESS)
            )
          );
          forkVisible = false;
        }
      } else if (p < DROP_OHT_RELEASE) {
        // —— DROP: OHT lowers FOUP onto external load port ——
        visible = false;
        resting = {};
        rackDisplay = startCount;
        carriage = [parkX, parkY, aisleZ];
        forkZ = aisleZ;
        forkVisible = false;
      } else if (p < DROP_PORT_REST_END) {
        // —— DROP: FOUP rides the external lifter from track down to deck ——
        visible = false;
        resting = {};
        rackDisplay = startCount;
        const ready = ease((p - DROP_OHT_RELEASE) / (DROP_PORT_REST_END - DROP_OHT_RELEASE));
        carriage = lerp3([parkX, parkY, aisleZ], [portPose[0], portPose[1], aisleZ], ready);
        forkZ = aisleZ;
        forkVisible = false;
      } else {
        // —— DROP: crane picks from port and stores into rack ——
        const u = (p - DROP_PORT_REST_END) / Math.max(0.001, 1 - DROP_PORT_REST_END);
        const motion = resolveStoreCrane(u, portPose, slotPose, aisleZ, forkDeck);
        pos = motion.foup;
        resting = {};
        carriage = motion.carriage;
        forkZ = motion.forkZ;
        forkVisible = motion.forkVisible;
        carrying = motion.carrying;
        // Keep the port-tray FOUP visible until the pick blend finishes, then hand off to StockerRobot.
        robotOwnsFoup = driveRobot && u >= 0.26 && motion.carrying;

        if (u >= 1) {
          // Full motion completed — atomically replace the moving carrier with
          // the destination slot FOUP and update the stocker count.
          visible = false;
          rackDisplay = committedDropRackCount(p, startCount, rackCount, totalSlots);
        } else {
          visible = true;
          rackDisplay = startCount;
        }
      }

      markCraneCompleteIfReady(
        active.progress,
        active.isPickup,
        active.eventId,
        active.stationId,
        active.transferPortId
      );

      cranePoseRef.current = {
        carriage,
        forkZ,
        forkVisible,
        active: true,
      };
      if (driveRobot && elementId) {
        stockerTransferRobotStore.set(
          elementId,
          mapTransferMotionToRobotState(
            {
              carriage,
              forkZ,
              forkVisible,
              carrying: robotOwnsFoup || (!driveRobot && carrying),
            },
            dims,
            width,
            active,
            aisleZ
          )
        );
      }
    } else {
      cranePoseRef.current = {
        carriage: [parkX, parkY, aisleZ],
        forkZ: aisleZ,
        forkVisible: false,
        active: false,
      };
      rackDisplay = rackCount;
      if (driveRobot && elementId) {
        stockerTransferRobotStore.set(elementId, resolveStockerRobotParkState(dims));
      }
    }

    const publishRackDisplay = (count: number) => {
      if (elementId) stockerRackDisplayStore.set(elementId, count);
      onRackRef.current?.(count);
    };

    if (rackDisplay !== lastRackRef.current) {
      lastRackRef.current = rackDisplay;
      publishRackDisplay(rackDisplay);
    }

    // Sync idle rack to inventory — but inventory socket can lag after DROP.
    if (!active && rackDisplay !== rackCount) {
      if (rackCount < lastRackRef.current) {
        // Authoritative count still stale; keep the crane-committed rack visible.
      } else {
        lastRackRef.current = rackCount;
        publishRackDisplay(rackCount);
      }
    }

    // Lifter transfer state mapping for StockerExternalLoadPort / FabAmhsLifter3D:
    let lifterState: StockerLifterTransferState = STOCKER_LIFTER_IDLE;
    if (active) {
      const p = active.progress;
      if (active.isPickup) {
        // PICKUP (Shelf -> Deck -> Track -> OHT):
        // 0.00..0.45: Crane brings FOUP from shelf to load port deck
        // 0.45..0.75: Lifter lifts FOUP to track; OHT takes at 0.75
        // 0.75..1.00: Empty lifter returns
        const portRest: Partial<Record<StockerLoadPortKind, boolean>> = {};
        let lifterProg = 0;
        let lifterTransferring = false;
        let hasFoupOnLifter = false;
        if (p >= PICKUP_PORT_READY && p + 1e-6 < STOCKER_PICKUP_OHT_TAKE_PROGRESS) {
          const u =
            (p - PICKUP_PORT_READY) /
            Math.max(1e-6, STOCKER_PICKUP_OHT_TAKE_PROGRESS - PICKUP_PORT_READY);
          lifterProg = 0.4 + u * 0.35;
          lifterTransferring = true;
          hasFoupOnLifter = true;
        } else if (p + 1e-6 >= STOCKER_PICKUP_OHT_TAKE_PROGRESS && p < 1) {
          const u =
            (p - STOCKER_PICKUP_OHT_TAKE_PROGRESS) /
            Math.max(1e-6, 1 - STOCKER_PICKUP_OHT_TAKE_PROGRESS);
          lifterProg = 0.75 + u * 0.25;
          lifterTransferring = true;
          hasFoupOnLifter = false;
        }

        lifterState = {
          isTransferring: lifterTransferring,
          activePortKind: kitKind,
          transferProgress01: lifterProg,
          transferOperation: 'PICKUP',
          foupPresent: hasFoupOnLifter,
          occupiedPorts: portRest,
        };
      } else {
        // DROP (OHT -> Track -> Lifter -> Deck -> Shelf):
        const portRest: Partial<Record<StockerLoadPortKind, boolean>> = {};

        let lifterProg = 0;
        let lifterTransferring = false;
        let hasFoupOnLifter = false;
        if (p + 1e-6 < DROP_OHT_RELEASE) {
          lifterProg = (p / Math.max(1e-6, DROP_OHT_RELEASE)) * 0.15;
          lifterTransferring = true;
        } else if (p < DROP_PORT_REST_END) {
          const u =
            (p - DROP_OHT_RELEASE) /
            Math.max(1e-6, DROP_PORT_REST_END - DROP_OHT_RELEASE);
          lifterProg = 0.15 + u * 0.85;
          lifterTransferring = true;
          hasFoupOnLifter = true;
        } else if (p >= DROP_PORT_REST_END) {
          lifterProg = 1;
          hasFoupOnLifter = false;
        }

        lifterState = {
          isTransferring: lifterTransferring,
          activePortKind: kitKind,
          transferProgress01: lifterProg,
          transferOperation: 'DROP',
          foupPresent: hasFoupOnLifter,
          occupiedPorts: portRest,
        };
      }
    }

    if (elementId) {
      stockerTransferLifterStore.set(elementId, lifterState);
    }

    const restKey = Object.keys(resting).sort().join(',');
    if (restKey !== occupiedKeyRef.current) {
      occupiedKeyRef.current = restKey;
      setOccupiedPorts(resting);
    }
    const showCarrier = shouldShowTransferCarrierFoup(visible, robotOwnsFoup, driveRobot);
    if (showCarrier !== visibleRef.current) {
      visibleRef.current = showCarrier;
      setCarrierVisible(showCarrier);
    }

    if (carrierRef.current) {
      if (pos && showCarrier) {
        carrierRef.current.position.set(pos[0], pos[1], pos[2]);
        carrierRef.current.scale.setScalar(scale);
        carrierRef.current.visible = true;
      } else {
        carrierRef.current.visible = false;
      }
    }
  });

  return (
    <group name="StockerTransferAnim">
      {showLoadPortBank && (
        <StockerLoadPortBank
          width={width}
          height={height}
          depth={depth}
          dims={dims}
          opacity={opacity}
          bayCount={bayCount}
          outsideStocker={outsideStocker}
          layoutPorts={layoutPorts}
          occupiedPorts={occupiedPorts}
          stockerId={elementId}
        />
      )}
      {showCrane ? (
        <StockerCrane
          width={width}
          height={height}
          depth={depth}
          opacity={opacity}
          foupScale={foupScale}
          poseRef={cranePoseRef}
        />
      ) : null}
      <group ref={carrierRef} visible={carrierVisible}>
        <FOUP scale={1} position={[0, 0, 0]} opacity={opacity} />
      </group>
    </group>
  );
};

export default StockerTransferAnim;
